import React from "react"
import types from "prop-types"
import { isEmpty } from "lodash-es"
import { useDispatch, useSelector } from "react-redux"
import * as API from "services/api"
import { isFeatureEnabled } from "reduxSlices/featuresSlice"
import { getFormBuilderPermissions, removeForm } from "reduxSlices/formIndexSlice"
import { shortMonthDate } from "utils/dateHelpers"
import { ORG_FORM_COPYING, ORG_FORM_DISCARDING } from "utils/featuresHelpers"
import { errorToast, successToast } from "shared/toast"
import Tooltip from "components/shared/Tooltip"
import {
  Tile, TileContent, TileFooter, TileHeader,
} from "shared/tiles"
import useModal from "hooks/useModal"
import CopyFormModal from "./CopyFormModal"
import FormMenu from "./FormMenu"
import FormTileIcon from "./FormTileIcon"
import ActiveToggle from "./ActiveToggle"

const tooltipText = (publisher) => {
  if (isEmpty(publisher)) return null

  return `Latest version by ${publisher.attributionName}`
}

const FormTile = ({ form }) => {
  const dispatch = useDispatch()
  const { modalIsOpen: copyModalIsOpen, openModal: openCopyModal, closeModal: closeCopyModal } = useModal()
  const { formBuilder: userIsAFormBuilder } = useSelector(getFormBuilderPermissions)
  const orgFormCopyingEnabled = useSelector(isFeatureEnabled(ORG_FORM_COPYING))
  const orgFormDiscardingEnabled = useSelector(isFeatureEnabled(ORG_FORM_DISCARDING))

  const userCanCopy = orgFormCopyingEnabled && userIsAFormBuilder
  const userCanDelete = orgFormDiscardingEnabled && form.userCanEdit && form.discardable

  const deleteForm = async () => {
    if (window.confirm("Are you sure you want to delete this form?")) {
      const response = await API.deleteForm({ formSlug: form.slug })

      if (response.ok) {
        dispatch(removeForm({ id: form.id }))
        successToast("Form deleted successfully.")
      } else {
        errorToast("Something went wrong. Unable to delete the form.")
      }
    }
  }

  return (
    <>
      <Tile>
        <TileHeader>
          <FormMenu
            formSlug={form.slug}
            onCopy={openCopyModal}
            onDelete={deleteForm}
            userCanCopy={userCanCopy}
            userCanDelete={userCanDelete}
            userCanEdit={form.userCanEdit}
          />
        </TileHeader>
        <TileContent className="px-8">
          <FormTileIcon form={form} />
          <div className="font-bold mb-1">{form.title}</div>
          <div className="font-bold text-sm text-gray-800 mb-2">{form.categoryName}</div>
          <div className="text-sm font-semibold leading-relaxed max-h-60 overflow-y-auto">{form.description}</div>
        </TileContent>
        <TileFooter>
          <div className="font-bold text-sm text-gray-800 mr-3 cursor-default">
            <Tooltip text={tooltipText(form.currentPublisher)}>
              {`Last modified on ${shortMonthDate(form.updatedAt)}`}
            </Tooltip>
          </div>
          <ActiveToggle form={form} />
        </TileFooter>
      </Tile>
      <CopyFormModal
        formCategoryId={form.categoryId}
        formSlug={form.slug}
        isOpen={copyModalIsOpen}
        onClose={closeCopyModal}
      />
    </>
  )
}

FormTile.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  form: types.object.isRequired,
}

export default FormTile
